import { useMemo } from 'react';
import NextImage from 'next/image';
import { Button, Image, Text } from '@mantine/core';
import { SUDS_METADATA } from '@/components/markers/SuggestedSudsDrawer/SudsMetadata';
import styles from './popover.module.css';

export interface PopoverContent {
  title: (feature: { feature: any }) => React.ReactElement;
  details: (feature: any, handleMoreDetailsClicked?: () => void) => React.ReactElement;
}

export class CsoPopoverContent implements PopoverContent {
  title({ feature }: { feature: any }): React.ReactElement {
    return (
      <>
        <Text size="sm" fw="600">
          CSO: {feature.properties.receivingWaterCourse}
        </Text>
      </>
    );
  }

  details({ feature }: { feature: any }): React.ReactElement {
    return (
      <div className={styles.popoverContent}>
        <div className={styles.popoverLeftCol}>
          <div className={styles.textGroup}>
            <Text className={styles.titleText}>Company</Text>
            <Text className={styles.regularText}>
              {feature.properties.company || 'Company unavailable'}
            </Text>
          </div>
          <div className={styles.textGroup}>
            <Text className={styles.titleText}>ID</Text>
            <Text className={styles.regularText}>{feature.properties.id || 'ID unavailable'}</Text>
          </div>
          <div className={styles.textGroup}>
            <Text className={styles.titleText}>Object ID</Text>
            <Text className={styles.regularText}>
              {feature.properties.objectId || 'Object ID unavailable'}
            </Text>
          </div>
        </div>
        <div className={styles.popoverRightCol}>
          <div className={styles.textGroup}>
            <Text className={styles.titleText}>Status</Text>
            <Text className={styles.regularText}>
              {feature.properties.status || 'Status unavailable'}
            </Text>
          </div>

          <div className={styles.textGroup}>
            <Text className={styles.titleText}>Receiving Water Course</Text>
            <Text className={styles.regularText}>
              {feature.properties.receivingWaterCourse || 'Not available'}
            </Text>
          </div>
        </div>
      </div>
    );
  }
}

export class SudsPopoverContent implements PopoverContent {
  title({ feature }: { feature: any }): React.ReactElement {
    return (
      <>
        <Text size="sm" fw="600">
          {feature.properties.sudsType || 'SuDS type unavailable'}:{' '}
          {feature.properties.location || 'Location unavailable'}
        </Text>
      </>
    );
  }

  details({ feature }: { feature: any }): React.ReactElement {
    return (
      <div className={styles.popoverContent}>
        <div className={styles.popoverLeftCol}>
          <div className={styles.textGroup}>
            <Text className={styles.titleText}>SuDS Type</Text>
            <Text className={styles.regularText}>
              {feature.properties.sudsSubType || 'SuDS subtype unavailable'}
            </Text>
          </div>

          <div className={styles.textGroup}>
            <Text className={styles.titleText}>Location</Text>
            <Text className={styles.regularText}>
              {feature.properties.location || 'Location unavailable'}
            </Text>
          </div>

          {/* Impact Summary */}
          {/* <div className={styles.textGroup}>
                <Text className={styles.titleText}>Impact Summary</Text>
                <Text className={styles.regularText}>Reduced runoff by 10%</Text>
              </div> */}
        </div>

        <div className={styles.popoverRightCol}>
          <div className={styles.textGroup}>
            <Text className={styles.titleText}>Description</Text>
            <Text className={styles.regularText}>
              {feature.properties.description || 'No description available'}
            </Text>
          </div>
        </div>
      </div>
    );
  }
}

export class SuggestedSudsPopoverContent implements PopoverContent {
  title({ feature }: { feature: any }): React.ReactElement {
    return (
      <>
        <Text size="sm" fw="600">
          {feature.properties['SuDS Type'] || 'SuDS type unavailable'}
        </Text>
      </>
    );
  }

  details({
    feature,
    handleMoreDetailsClicked,
  }: {
    feature: any;
    handleMoreDetailsClicked?: () => void;
  }): React.ReactElement {
    const extraDetails = useMemo(() => {
      if (feature.properties && feature.properties['SuDS Type']) {
        const type = feature.properties['SuDS Type'];
        if (['Green roof', 'Swale', 'Detention basin', 'Rain garden'].includes(type)) {
          return SUDS_METADATA[type as keyof typeof SUDS_METADATA];
        }
      }
      return null;
    }, [feature]);

    return (
      <div className={styles.popoverContent}>
        <div className={styles.popoverLeftCol}>
          <div className={styles.textGroup}>
            <Text className={styles.titleText}>SuDS Type</Text>
            <Text className={styles.regularText}>
              {extraDetails?.displayName || 'SuDS subtype unavailable'}
            </Text>
          </div>
          <div className={styles.textGroup}>
            <Text className={styles.titleText}>Distance to CSO</Text>
            <Text className={styles.regularText}>
              {feature.properties.dist_to_csomean
                ? `${feature.properties.dist_to_csomean.toFixed(1)} m`
                : 'Distance unavailable'}
            </Text>
          </div>
          <div className={styles.textGroup}>
            <Text className={styles.titleText}>Primary Function</Text>
            <Text className={styles.regularText}>
              {extraDetails?.primaryFunction || 'Primary Function unavailable'}
            </Text>
          </div>
        </div>
        <div className={styles.popoverRightCol}>
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              gap: 16,
            }}
          >
            {extraDetails?.img && (
              <Image
                component={NextImage}
                src={extraDetails.img}
                alt="SuDS Image"
                h={120}
                w="100%"
                fit="cover"
              />
            )}
            <Button variant="outline" w="100%" onClick={handleMoreDetailsClicked}>
              More Details
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export const popoverContent = {
  suds: new SudsPopoverContent(),
  suggestedSuds: new SuggestedSudsPopoverContent(),
  cso: new CsoPopoverContent(),
};
